import { useMutation } from '@tanstack/react-query';
import {
  formatDecimal,
  formatMoney,
  formatQuantity,
  type CostOption,
  type Margin,
  type MarginLight,
  parseDecimal,
  type PlannedCost,
  type PlannedCostRequest,
  type QuoteCosts,
  type QuoteResponse,
} from '@k-management/shared';
import { type FocusEvent, Fragment, type KeyboardEvent, useState } from 'react';
import { DecimalInput } from '../../components/DecimalInput.js';
import { apiRequest } from '../../lib/api.js';
import { entryLabel, LicenceLightBadge, useQuoteLookups } from './quotes-ui.js';

const LIGHT_LABELS: Record<MarginLight, string> = {
  green: 'verde',
  yellow: 'giallo',
  red: 'rosso',
};

/** «39,5 %» with the colour of the traffic light. */
export function MarginBadge({ margin }: { margin: Margin }) {
  if (margin.percent === null || margin.light === null) return <>—</>;
  const percent = formatDecimal(margin.percent, 1, 1);
  return (
    <span
      className={`margin-light ${margin.light}`}
      title={`Semaforo ${LIGHT_LABELS[margin.light]}`}
    >
      <span className="dot" aria-hidden="true" />
      {percent} %
    </span>
  );
}

/** How far the line is from the green: what is missing, or how much can still go. */
function GreenHint({ margin }: { margin: Margin }) {
  const sold = parseDecimal(margin.sold) ?? 0;
  const target = parseDecimal(margin.greenPrice) ?? 0;
  return (
    <>
      {formatMoney(margin.greenPrice)}
      <small className="muted block">
        {sold >= target
          ? `si può scendere di ${formatMoney(sold - target)}`
          : `mancano ${formatMoney(target - sold)}`}
      </small>
    </>
  );
}

/**
 * Costi previsti (decisions of 26 and 28/09/2026): every line sold but the
 * licences takes one or more estimated costs, each with its own quantity and
 * unit (a senior and a junior in the same classroom), and the quote takes
 * transversal costs (fees, travels) that weigh on the total only. From the
 * table Costi or written by hand; the margin with its traffic light is a
 * range for the commercials to move in, not the management control.
 */
export function CostsView({
  quote,
  costs,
  canWrite,
  onChanged,
}: {
  quote: QuoteResponse;
  costs: QuoteCosts;
  canWrite: boolean;
  onChanged: (updated: QuoteResponse) => Promise<void>;
}) {
  const lookups = useQuoteLookups();
  const units = lookups.data?.units;
  const hasLicences = quote.lines.length > costs.lines.length;
  const linesCost = costs.lines.reduce(
    (sum, line) => sum + (line.cost ? (parseDecimal(line.cost) ?? 0) : 0),
    0,
  );
  const columns = canWrite ? 8 : 7;
  const edit = { quoteId: quote.id, units, canWrite, onChanged };
  // The line sold whose first cost is being written (its ✎).
  const [filling, setFilling] = useState<string | null>(null);

  return (
    <section className="card stack">
      <div className="cost-kpis">
        <div className="cost-kpi">
          <small>Vendita netta (righe con costo)</small>
          <strong>{costs.total ? formatMoney(costs.total.sold) : '—'}</strong>
        </div>
        <div className="cost-kpi">
          <small>Costi delle righe</small>
          <strong>{formatMoney(linesCost)}</strong>
        </div>
        <div className="cost-kpi">
          <small>Costi trasversali</small>
          <strong>{formatMoney(costs.transversal.total)}</strong>
        </div>
        <div className="cost-kpi">
          <small>Margine</small>
          <strong>{costs.total ? formatMoney(costs.total.margin) : '—'}</strong>
        </div>
        <div className={`cost-kpi ${costs.total?.light ?? ''}`}>
          <small>Margine %</small>
          <strong>{costs.total ? <MarginBadge margin={costs.total} /> : '—'}</strong>
        </div>
        {costs.expenses.light && (
          <div className={`cost-kpi ${costs.expenses.light === 'verde' ? 'green' : 'red'}`}>
            <small>Spese, fuori dal margine</small>
            <strong>
              {formatMoney(costs.expenses.passive)} su {formatMoney(costs.expenses.active)}
            </strong>
          </div>
        )}
      </div>

      <div className="table-scroll">
        <table className="lines costs">
          <thead>
            <tr>
              <th>Riga di vendita / costo</th>
              <th className="right">Q.tà</th>
              <th className="right">Costo unitario</th>
              <th className="right">Vendita netta</th>
              <th className="right">Costo totale</th>
              <th className="right">Margine</th>
              <th className="right">Per il verde ({costs.thresholds.green} %)</th>
              {canWrite && <th />}
            </tr>
          </thead>
          <tbody>
            {costs.lines.length === 0 && (
              <tr>
                <td colSpan={columns} className="muted">
                  Non ci sono righe con costi: le licenze restano fuori dal margine.
                </td>
              </tr>
            )}
            {costs.lines.map((line) => (
              <Fragment key={line.lineId}>
                <tr className={line.costs.length > 0 ? 'sale-row' : 'sale-row missing'}>
                  <td>{line.label}</td>
                  <td className="right">
                    {line.editions > 1 && `${line.editions} edizioni × `}
                    {formatQuantity(line.quantity)} {entryLabel(units, line.unitCode).toLowerCase()}
                    {line.editions > 1 && (
                      <small className="muted block">i costi valgono per edizione</small>
                    )}
                  </td>
                  <td />
                  <td className="right">{formatMoney(line.sold)}</td>
                  <td className="right">
                    {line.cost ? formatMoney(line.cost) : <span className="pill bad">manca</span>}
                  </td>
                  <td className="right">
                    {line.margin ? (
                      <>
                        {formatMoney(line.margin.margin)}
                        <br />
                        <MarginBadge margin={line.margin} />
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="right">
                    {line.margin ? <GreenHint margin={line.margin} /> : '—'}
                  </td>
                  {canWrite && (
                    <td className="row-actions">
                      {line.costs.length === 0 && filling !== line.lineId && (
                        <button
                          type="button"
                          className="small"
                          title="Scrivi il costo di questa riga"
                          aria-label={`Scrivi il costo di ${line.label}`}
                          onClick={() => setFilling(line.lineId)}
                        >
                          ✎
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {line.costs.map((cost) => (
                  <CostRow
                    key={cost.id}
                    cost={cost}
                    options={line.options}
                    columns={columns}
                    {...edit}
                  />
                ))}
                {canWrite && line.costs.length === 0 && filling === line.lineId && (
                  <DraftCost
                    onClose={() => setFilling(null)}
                    lineId={line.lineId}
                    label={line.label}
                    options={line.options}
                    quantity={line.quantity}
                    unitCode={line.unitCode}
                    columns={columns}
                    {...edit}
                  />
                )}
                {canWrite && (
                  <AddCost
                    lineId={line.lineId}
                    label={line.label}
                    options={line.options}
                    quantity={line.quantity}
                    unitCode={line.unitCode}
                    columns={columns}
                    {...edit}
                  />
                )}
              </Fragment>
            ))}

            <tr className="section-row">
              <td colSpan={columns}>
                Costi trasversali{' '}
                <small className="muted">
                  · fee, trasferte, terze parti: non stanno su una riga, pesano solo sul totale
                </small>
              </td>
            </tr>
            {costs.transversal.costs.map((cost) => (
              <CostRow
                key={cost.id}
                cost={cost}
                options={costs.transversal.options}
                columns={columns}
                {...edit}
              />
            ))}
            {canWrite && (
              <AddCost
                lineId={null}
                label="costi trasversali"
                options={costs.transversal.options}
                quantity="1"
                unitCode="CORPO"
                columns={columns}
                {...edit}
              />
            )}
            {!canWrite && costs.transversal.costs.length === 0 && (
              <tr>
                <td colSpan={columns} className="muted">
                  Nessun costo trasversale.
                </td>
              </tr>
            )}

            {costs.total && (
              <tr className="total-row">
                <td>Totale (righe con costo e costi trasversali)</td>
                <td />
                <td />
                <td className="right">{formatMoney(costs.total.sold)}</td>
                <td className="right">{formatMoney(costs.total.cost)}</td>
                <td className="right">
                  {formatMoney(costs.total.margin)}
                  <br />
                  <MarginBadge margin={costs.total} />
                </td>
                <td className="right">
                  <GreenHint margin={costs.total} />
                </td>
                {canWrite && <td />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {hasLicences && quote.licenceLights && (
        <div className="table-scroll">
          <table className="lines costs">
            <thead>
              <tr>
                <th>Licenze</th>
                <th className="right">Listino</th>
                <th className="right">Venduto</th>
                <th className="right">Sul listino</th>
                <th className="right">Limite del giallo</th>
              </tr>
            </thead>
            <tbody>
              {quote.licenceLights.lines.map((line) => (
                <tr key={line.lineId}>
                  <td>{line.label}</td>
                  <td className="right">{formatMoney(line.listPrice)}</td>
                  <td className="right">{formatMoney(line.sold)}</td>
                  <td className="right">
                    <LicenceLightBadge light={line} />
                  </td>
                  <td className="right">{formatMoney(line.yellowFloor)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Totale licenze</td>
                <td className="right">{formatMoney(quote.licenceLights.total.listPrice)}</td>
                <td className="right">{formatMoney(quote.licenceLights.total.sold)}</td>
                <td className="right">
                  <LicenceLightBadge light={quote.licenceLights.total} />
                </td>
                <td className="right">{formatMoney(quote.licenceLights.total.yellowFloor)}</td>
              </tr>
            </tbody>
          </table>
          <p className="muted small">
            Le licenze non hanno costi: il semaforo guarda il prezzo rispetto al listino (senza il
            premium, con lo sconto sul documento). Soglie in Tabelle → Soglie licenze.
          </p>
        </div>
      )}
      <p className="muted small">
        Verde da {costs.thresholds.green} %, giallo da {costs.thresholds.yellow} %, rosso sotto.
        Margine % = (vendita − costo) ÷ vendita; per il verde = costo ÷ (1 −{' '}
        {costs.thresholds.green} %). La vendita netta toglie lo sconto della riga e la sua parte
        dello sconto sul documento. Ogni riga ha i suoi costi, ciascuno con quantità e unità
        proprie; i costi trasversali entrano solo nel totale. Le voci del Listino Passività senza
        importo non compaiono finché non lo si scrive. Le soglie stanno in Tabelle → Soglie di
        margine.
      </p>
    </section>
  );
}

interface EditProps {
  quoteId: string;
  units: { code: string; description: string }[] | undefined;
  canWrite: boolean;
  onChanged: (updated: QuoteResponse) => Promise<void>;
  columns: number;
}

/** Calls the API on the costs and hands the updated quote back. */
function useCostMutation(onChanged: EditProps['onChanged'], done: () => void) {
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: ({
      url,
      method,
      body,
    }: {
      url: string;
      method: 'POST' | 'PUT' | 'DELETE';
      body?: unknown;
    }) => apiRequest<QuoteResponse>(url, { method, body }),
    onSuccess: async (updated) => {
      setError('');
      done();
      await onChanged(updated);
    },
    onError: (failure: Error) => setError(failure.message),
  });
  return { mutation, error };
}

/** Entering a cell selects its value: what is typed replaces it. */
/** Selects the number on focus, so the first key typed replaces it. */
export function selectAll(event: FocusEvent<HTMLInputElement>) {
  const input = event.currentTarget;
  // After the number is shown as typed; never over what was already typed.
  // Selected at once and again after the number is shown as typed; the first
  // character typed replaces the value even when the selection got lost.
  let typed = false;
  input.select();
  input.addEventListener(
    'keydown',
    (event) => {
      const key = (event as { key?: string }).key ?? '';
      if (key.length === 1 && input.selectionStart === input.selectionEnd) input.select();
      typed = true;
    },
    { once: true },
  );
  requestAnimationFrame(() => {
    if (!typed) input.select();
  });
}

/**
 * Description, quantity and unit cost of a cost in its row: plain text to
 * read, cells to change after ✎ (decision of 29/09/2026). ✓ or Enter saves,
 * ✕ or Esc puts the values back.
 */
function CostCells({
  description,
  quantity,
  unitCost,
  unit,
  label,
  pending,
  autoFocus,
  onDescription,
  onQuantity,
  onUnitCost,
  onSave,
  onCancel,
}: {
  description: string;
  quantity: string;
  unitCost: string;
  unit: string;
  label: string;
  pending: boolean;
  autoFocus?: 'description' | 'unitCost';
  onDescription: (value: string) => void;
  onQuantity: (value: string) => void;
  onUnitCost: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSave();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };
  return (
    <>
      <td>
        <input
          className="cell-input"
          aria-label={`Descrizione del costo di ${label}`}
          maxLength={200}
          placeholder="Che cosa costa (es. Formatore senior)"
          value={description}
          disabled={pending}
          autoFocus={autoFocus === 'description'}
          onChange={(event) => onDescription(event.target.value)}
          onFocus={selectAll}
          onKeyDown={keys}
        />
      </td>
      <td className="right nowrap">
        <DecimalInput
          className="cell-input number"
          aria-label={`Quantità del costo di ${label}`}
          decimals={[0, 3]}
          value={quantity}
          disabled={pending}
          onChange={onQuantity}
          onFocus={selectAll}
          onKeyDown={keys}
        />{' '}
        {unit}
      </td>
      <td className="right">
        <DecimalInput
          className="cell-input number"
          aria-label={`Costo unitario di ${label}`}
          decimals={[2, 2]}
          placeholder="costo"
          value={unitCost}
          disabled={pending}
          autoFocus={autoFocus === 'unitCost'}
          onChange={onUnitCost}
          onFocus={selectAll}
          onKeyDown={keys}
        />
      </td>
    </>
  );
}

/** ✓ and ✕ of a row being changed. */
function SaveCancel({
  label,
  pending,
  onSave,
  onCancel,
}: {
  label: string;
  pending: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <button
        type="button"
        className="small primary"
        title="Salva (Invio)"
        aria-label={`Salva il costo di ${label}`}
        disabled={pending}
        onClick={onSave}
      >
        ✓
      </button>
      <button
        type="button"
        className="small"
        title="Annulla (Esc)"
        aria-label={`Annulla le modifiche a ${label}`}
        disabled={pending}
        onClick={onCancel}
      >
        ✕
      </button>
    </>
  );
}

function CostRow({
  quoteId,
  cost,
  options,
  units,
  canWrite,
  onChanged,
  columns,
}: EditProps & { cost: PlannedCost; options: CostOption[] }) {
  const [mode, setMode] = useState<'view' | 'cells' | 'voice'>('view');
  const { mutation, error } = useCostMutation(onChanged, () => setMode('view'));
  const unit = entryLabel(units, cost.unitCode).toLowerCase();
  const url = `/quotes/${quoteId}/costs/${cost.id}`;
  const [description, setDescription] = useState(cost.description);
  const [quantity, setQuantity] = useState(cost.quantity);
  const [unitCost, setUnitCost] = useState(cost.unitCost);
  const edit = () => {
    setDescription(cost.description);
    setQuantity(cost.quantity);
    setUnitCost(cost.unitCost);
    // A fee as a percentage changes its percentage in the form, not in the cells.
    setMode(cost.percent !== null ? 'voice' : 'cells');
  };
  const same = (a: string, b: string) => parseDecimal(a) === parseDecimal(b);
  const save = () => {
    if (
      description.trim() === cost.description &&
      same(quantity, cost.quantity) &&
      same(unitCost, cost.unitCost)
    ) {
      setMode('view');
      return;
    }
    const body: PlannedCostRequest = cost.code
      ? { quantity, code: cost.code, description, unitCost }
      : { quantity, description, unitCode: cost.unitCode, unitCost };
    mutation.mutate({ url, method: 'PUT', body });
  };
  // A voice of the table changed for this quote: its own values, the table's beside them.
  const table = cost.table;
  // «Fee %» and «Fee a corpo» are written in the quote: nothing to compare with.
  const openVoice = table !== null && table.percent === null && table.unitCost === null;
  const changed =
    table !== null &&
    !openVoice &&
    (table.description !== cost.description ||
      (cost.percent !== null
        ? table.percent !== null && !same(table.percent, cost.percent)
        : table.unitCost !== null && !same(table.unitCost, cost.unitCost)));

  if (mode === 'voice') {
    return (
      <tr className="cost-row editing-row">
        <td colSpan={columns}>
          <CostForm
            options={options}
            units={units}
            initial={cost}
            pending={mutation.isPending}
            error={error}
            onSubmit={(body) => mutation.mutate({ url, method: 'PUT', body })}
            onCancel={() => setMode('view')}
          />
        </td>
      </tr>
    );
  }
  if (mode === 'cells') {
    return (
      <tr className="cost-row draft-row">
        <CostCells
          description={description}
          quantity={quantity}
          unitCost={unitCost}
          unit={unit}
          label={cost.description}
          pending={mutation.isPending}
          autoFocus="unitCost"
          onDescription={setDescription}
          onQuantity={setQuantity}
          onUnitCost={setUnitCost}
          onSave={save}
          onCancel={() => setMode('view')}
        />
        <td colSpan={columns - 4} className="small">
          <button type="button" className="link" onClick={() => setMode('voice')}>
            Cambia voce o unità
          </button>
          {error && <small className="field-error block">{error}</small>}
        </td>
        <td className="row-actions">
          <SaveCancel
            label={cost.description}
            pending={mutation.isPending}
            onSave={save}
            onCancel={() => setMode('view')}
          />
        </td>
      </tr>
    );
  }
  return (
    <tr className="cost-row">
      <td>
        {cost.description} <small className="muted nowrap">{cost.code ?? 'personalizzato'}</small>
        {cost.isExpense && <span className="pill info">spesa</span>}
        {canWrite && cost.percent === null && (
          <label className="checkbox small block">
            <input
              type="checkbox"
              checked={cost.isExpense}
              disabled={mutation.isPending}
              onChange={(event) =>
                mutation.mutate({
                  url,
                  method: 'PUT',
                  body: cost.code
                    ? {
                        quantity: cost.quantity,
                        code: cost.code,
                        description: cost.description,
                        unitCost: cost.unitCost,
                        isExpense: event.target.checked,
                      }
                    : {
                        quantity: cost.quantity,
                        description: cost.description,
                        unitCode: cost.unitCode,
                        unitCost: cost.unitCost,
                        isExpense: event.target.checked,
                      },
                })
              }
            />{' '}
            spesa: fuori dal margine
          </label>
        )}
        {changed && (
          <small className="muted block">
            <span className="pill info">modificato</span> tabella: {table!.description}
            {table!.percent !== null
              ? ` · ${table!.percent} %`
              : table!.unitCost !== null
                ? ` · ${formatMoney(table!.unitCost)}`
                : ''}
          </small>
        )}
        {error && <small className="field-error block">{error}</small>}
      </td>
      {cost.percent !== null ? (
        // A fee as a percentage: worked out on the taxable amount of the quote.
        <>
          <td className="right">{cost.percent.replace('.', ',')} %</td>
          <td className="right muted small">dell’imponibile</td>
        </>
      ) : (
        <>
          <td className="right">
            {formatQuantity(cost.quantity)} {unit}
          </td>
          <td className="right">{formatMoney(cost.unitCost)}</td>
        </>
      )}
      <td />
      <td className="right">{formatMoney(cost.total)}</td>
      <td />
      <td />
      {canWrite && (
        <td className="row-actions">
          {changed && (
            <button
              type="button"
              className="small"
              title="Riporta descrizione e costo della tabella"
              aria-label={`Riporta i valori della tabella per ${cost.description}`}
              disabled={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  url,
                  method: 'PUT',
                  body: { quantity: cost.quantity, code: cost.code! },
                })
              }
            >
              ↺
            </button>
          )}
          <button
            type="button"
            className="small"
            title="Modifica descrizione, quantità e costo"
            aria-label={`Modifica ${cost.description}`}
            onClick={edit}
          >
            ✎
          </button>
          <button
            type="button"
            className="small"
            title="Togli il costo"
            aria-label={`Togli ${cost.description}`}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ url, method: 'DELETE' })}
          >
            🗑
          </button>
        </td>
      )}
    </tr>
  );
}

/**
 * The first cost of a line sold, opened by the ✎ of the line (decision of
 * 29/09/2026): the voice of the table Costi for the line if there is one, the
 * quantity of the line; ✓ or Enter saves it.
 */
function DraftCost({
  quoteId,
  lineId,
  label,
  options,
  quantity: lineQuantity,
  unitCode: lineUnit,
  units,
  onChanged,
  onClose,
  columns,
}: EditProps & {
  lineId: string;
  label: string;
  options: CostOption[];
  quantity: string;
  unitCode: string;
  onClose: () => void;
}) {
  const option = options.find((item) => item.unitCode === lineUnit) ?? options[0];
  const [description, setDescription] = useState(option?.description ?? '');
  const [quantity, setQuantity] = useState(lineQuantity);
  const [unitCost, setUnitCost] = useState(option?.unitCost ?? '');
  const [missing, setMissing] = useState('');
  const { mutation, error } = useCostMutation(onChanged, onClose);
  const unitCode = option?.unitCode ?? lineUnit;
  const save = () => {
    if (mutation.isPending) return;
    if (unitCost.trim() === '' || parseDecimal(quantity) === null) {
      setMissing('Scrivi quantità e costo unitario');
      return;
    }
    const text = description.trim() || label;
    mutation.mutate({
      url: `/quotes/${quoteId}/costs`,
      method: 'POST',
      body: option
        ? { lineId, quantity, code: option.code, description: text, unitCost }
        : { lineId, quantity, description: text, unitCode, unitCost },
    });
  };

  return (
    <tr className="cost-row draft-row">
      <CostCells
        description={description}
        quantity={quantity}
        unitCost={unitCost}
        unit={entryLabel(units, unitCode).toLowerCase()}
        label={label}
        pending={mutation.isPending}
        autoFocus={option ? 'unitCost' : 'description'}
        onDescription={setDescription}
        onQuantity={setQuantity}
        onUnitCost={setUnitCost}
        onSave={save}
        onCancel={onClose}
      />
      <td colSpan={columns - 4} className="muted small">
        {option ? `Dal Listino Passività (${option.code})` : 'Costo personalizzato'}
        {(error || missing) && <small className="field-error block">{error || missing}</small>}
      </td>
      <td className="row-actions">
        <SaveCancel label={label} pending={mutation.isPending} onSave={save} onCancel={onClose} />
      </td>
    </tr>
  );
}

function AddCost({
  quoteId,
  lineId,
  label,
  options,
  quantity,
  unitCode,
  units,
  onChanged,
  columns,
}: EditProps & {
  lineId: string | null;
  label: string;
  options: CostOption[];
  quantity: string;
  unitCode: string;
}) {
  const [open, setOpen] = useState(false);
  const { mutation, error } = useCostMutation(onChanged, () => setOpen(false));

  return (
    <tr className={open ? 'cost-row editing-row' : 'cost-row add-row'}>
      <td colSpan={columns}>
        {open ? (
          <CostForm
            options={options}
            units={units}
            initial={{ quantity, unitCode }}
            pending={mutation.isPending}
            error={error}
            hint={
              lineId
                ? 'Costi della stessa tipologia, in giorni o in ore.'
                : 'Tutte le voci del Listino Passività con un importo.'
            }
            onSubmit={(body) =>
              mutation.mutate({
                url: `/quotes/${quoteId}/costs`,
                method: 'POST',
                body: { lineId, ...body },
              })
            }
            onCancel={() => setOpen(false)}
          />
        ) : (
          <button
            type="button"
            className="small"
            aria-label={`Aggiungi un costo a ${label}`}
            onClick={() => setOpen(true)}
          >
            + Aggiungi costo
          </button>
        )}
      </td>
    </tr>
  );
}

/** A voice of the table Costi, or «Personalizzato» with description, unit and amount. */
function CostForm({
  options,
  units,
  initial,
  pending,
  error,
  hint,
  onSubmit,
  onCancel,
}: {
  options: CostOption[];
  units: { code: string; description: string }[] | undefined;
  initial: Partial<PlannedCost> & { quantity: string; unitCode: string };
  pending: boolean;
  error: string;
  hint?: string;
  onSubmit: (body: PlannedCostRequest) => void;
  onCancel: () => void;
}) {
  const firstChoice = initial.id
    ? (initial.code ?? 'custom')
    : (options.find((option) => option.unitCode === initial.unitCode)?.code ??
      options[0]?.code ??
      'custom');
  const [choice, setChoice] = useState(firstChoice);
  const [quantity, setQuantity] = useState(initial.quantity);
  const [description, setDescription] = useState(initial.code ? '' : (initial.description ?? ''));
  const [unitCode, setUnitCode] = useState(initial.unitCode);
  const [unitCost, setUnitCost] = useState(initial.code ? '' : (initial.unitCost ?? ''));
  const [percent, setPercent] = useState(initial.percent ?? '');
  const [voiceText, setVoiceText] = useState(initial.code ? (initial.description ?? '') : '');
  const option = options.find((item) => item.code === choice);
  // A fee (proposal Listini §6): a percentage, or an amount written here.
  const isPercent = option?.percent !== null && option?.percent !== undefined;
  const asksAmount = option !== undefined && !isPercent && option.unitCost === null;
  // A voice no longer in the table stays chosen, with what was copied into the quote.
  const kept = initial.code && !option && choice === initial.code ? initial : null;
  const unit = entryLabel(units, option?.unitCode ?? kept?.unitCode ?? unitCode).toLowerCase();

  return (
    <form
      className="cost-form"
      onSubmit={(event) => {
        event.preventDefault();
        const text = voiceText.trim();
        onSubmit(
          choice === 'custom'
            ? { quantity, description, unitCode, unitCost }
            : isPercent
              ? {
                  quantity: '1',
                  code: choice,
                  ...(percent.trim() ? { percent } : {}),
                  ...(text ? { description: text } : {}),
                }
              : asksAmount
                ? { quantity, code: choice, unitCost, ...(text ? { description: text } : {}) }
                : { quantity, code: choice },
        );
      }}
    >
      <label>
        Costo
        <select value={choice} onChange={(event) => setChoice(event.target.value)}>
          {options.map((item) => (
            <option key={item.code} value={item.code}>
              {item.description} ·{' '}
              {item.percent !== null
                ? item.percent
                  ? `${item.percent.replace('.', ',')} % dell’imponibile`
                  : '% da scrivere'
                : item.unitCost !== null
                  ? `${formatMoney(item.unitCost)} (${entryLabel(units, item.unitCode).toLowerCase()})`
                  : 'importo da scrivere'}
            </option>
          ))}
          {kept && <option value={kept.code!}>{kept.description} (non più in tabella)</option>}
          <option value="custom">Personalizzato…</option>
        </select>
      </label>
      {choice === 'custom' && (
        <>
          <label>
            Descrizione
            <input
              maxLength={200}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <label>
            Unità
            <select value={unitCode} onChange={(event) => setUnitCode(event.target.value)}>
              {(units ?? []).map((item) => (
                <option key={item.code} value={item.code}>
                  {item.description}
                </option>
              ))}
            </select>
          </label>
          <label>
            Costo unitario
            <DecimalInput
              className="number"
              decimals={[2, 2]}
              value={unitCost}
              onChange={setUnitCost}
            />
          </label>
        </>
      )}
      {(isPercent || asksAmount) && (
        <label>
          Descrizione
          <input
            maxLength={200}
            value={voiceText}
            placeholder={option?.description}
            onChange={(event) => setVoiceText(event.target.value)}
          />
        </label>
      )}
      {isPercent ? (
        <label>
          Percentuale dell’imponibile
          <DecimalInput
            className="number"
            decimals={[0, 3]}
            value={percent}
            placeholder={option?.percent || 'es. 2,5'}
            onChange={setPercent}
          />
        </label>
      ) : (
        <>
          {asksAmount && (
            <label>
              Importo
              <DecimalInput
                className="number"
                decimals={[2, 2]}
                value={unitCost}
                onChange={setUnitCost}
              />
            </label>
          )}
          <label>
            Quantità ({unit})
            <DecimalInput
              className="number"
              decimals={[0, 3]}
              value={quantity}
              onChange={setQuantity}
            />
          </label>
        </>
      )}
      <div className="cost-form-actions">
        <button type="submit" className="small primary" disabled={pending}>
          Salva
        </button>
        <button type="button" className="small" onClick={onCancel}>
          Annulla
        </button>
      </div>
      {options.length === 0 && choice === 'custom' && (
        <small className="muted full">
          Nessun costo in tabella: scrivilo con «Personalizzato».
        </small>
      )}
      {hint && options.length > 0 && <small className="muted full">{hint}</small>}
      {error && <small className="field-error full">{error}</small>}
    </form>
  );
}
